import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiUsers, FiArrowLeft, FiSave, FiAlertCircle, FiCheckCircle, FiActivity, FiTag } from 'react-icons/fi';
import { createDoctor } from './doctorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDepartments, getSpecialtiesByDepartment } from '../specialties/taxonomyService';
import './CreateDoctor.css';

export default function CreateDoctor() {
    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        guildId: '',
        departmentId: '',
        specialtyId: '',
        departmentName: '',
        specialtyName: '',
        membershipStartDate: '',
        membershipEndDate: '',
        education: [],
    });
    const [newEdu, setNewEdu] = useState('');
    const [guilds, setGuilds] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadInitialData();
    }, []);

    async function loadInitialData() {
        try {
            const [guildData, deptData] = await Promise.all([
                getAllGuilds(),
                getAllDepartments()
            ]);
            setGuilds(guildData);
            setDepartments(deptData);
        } catch (err) {
            console.error('Error loading initial data:', err);
        }
    }

    async function handleDepartmentChange(e) {
        const deptId = e.target.value;
        const dept = departments.find(d => d.id === deptId);

        setFormData({
            ...formData,
            departmentId: deptId,
            departmentName: dept ? dept.name : '',
            specialtyId: '',
            specialtyName: ''
        });

        if (deptId) {
            try {
                const specData = await getSpecialtiesByDepartment(deptId);
                setSpecialties(specData);
            } catch (err) {
                console.error('Error loading specialties:', err);
            }
        } else {
            setSpecialties([]);
        }
    }

    function handleSpecialtyChange(e) {
        const specId = e.target.value;
        const spec = specialties.find(s => s.id === specId);

        setFormData({
            ...formData,
            specialtyId: specId,
            specialtyName: spec ? spec.name : ''
        });
    }

    function handleChange(e) {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError(''); // Clear error on input change
    }

    function addEducation() {
        if (newEdu.trim()) {
            setFormData(prev => ({
                ...prev,
                education: [...prev.education, newEdu.trim()]
            }));
            setNewEdu('');
        }
    }

    function removeEducation(index) {
        setFormData(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.email || !formData.guildId || !formData.departmentId || !formData.specialtyId) {
            setError('Please fill in all required fields');
            return;
        }

        // Validate membership dates
        if (formData.membershipStartDate && formData.membershipEndDate) {
            if (new Date(formData.membershipEndDate) < new Date(formData.membershipStartDate)) {
                setError('Membership expiry date cannot be before the start date');
                return;
            }
        }

        try {
            setLoading(true);
            setError('');
            setSuccess(false);

            // Format dates for Firestore
            const submissionData = { ...formData };
            if (submissionData.membershipStartDate) submissionData.membershipStartDate = new Date(submissionData.membershipStartDate);
            if (submissionData.membershipEndDate) submissionData.membershipEndDate = new Date(submissionData.membershipEndDate);

            await createDoctor(submissionData);
            setSuccess(true);

            // Show success message then navigate
            setTimeout(() => {
                navigate('/dashboard/doctors');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to create doctor');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Create Doctor</h1>
                <button
                    type="button"
                    onClick={() => navigate('/dashboard/doctors')}
                    className="btn-secondary"
                >
                    <FiArrowLeft />
                    <span>Back to List</span>
                </button>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-error">
                            <FiAlertCircle />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            <FiCheckCircle />
                            <span>Doctor created successfully! Password reset email sent. Redirecting...</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="fullName">
                            <FiUsers />
                            <span>Full Name *</span>
                        </label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Dr. Jane Smith"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">
                            <FiMail />
                            <span>Email Address *</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="doctor@example.com"
                            required
                            disabled={loading}
                        />
                        <small className="form-hint">
                            Doctor will receive a password reset email to set up their account
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="guildId">
                            <FiUsers />
                            <span>Guild *</span>
                        </label>
                        <select
                            id="guildId"
                            name="guildId"
                            value={formData.guildId}
                            onChange={handleChange}
                            required
                            disabled={loading}
                        >
                            <option value="">Select a guild</option>
                            {guilds.map((guild) => (
                                <option key={guild.id} value={guild.id}>
                                    {guild.name}
                                </option>
                            ))}
                        </select>
                        <small className="form-hint">
                            Assign the doctor to a medical guild
                        </small>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label htmlFor="departmentId">
                                <FiActivity />
                                <span>Department *</span>
                            </label>
                            <select
                                id="departmentId"
                                name="departmentId"
                                value={formData.departmentId}
                                onChange={handleDepartmentChange}
                                required
                                disabled={loading}
                            >
                                <option value="">Select Department</option>
                                {departments.map((dept) => (
                                    <option key={dept.id} value={dept.id}>
                                        {dept.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="specialtyId">
                                <FiTag />
                                <span>Specialty *</span>
                            </label>
                            <select
                                id="specialtyId"
                                name="specialtyId"
                                value={formData.specialtyId}
                                onChange={handleSpecialtyChange}
                                required
                                disabled={loading || !formData.departmentId}
                            >
                                <option value="">Select Specialty</option>
                                {specialties.map((spec) => (
                                    <option key={spec.id} value={spec.id}>
                                        {spec.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label htmlFor="membershipStartDate">
                                <FiActivity />
                                <span>Membership Start Date</span>
                            </label>
                            <input
                                type="date"
                                id="membershipStartDate"
                                name="membershipStartDate"
                                value={formData.membershipStartDate}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="membershipEndDate">
                                <FiActivity />
                                <span>Membership Expiry Date</span>
                            </label>
                            <input
                                type="date"
                                id="membershipEndDate"
                                name="membershipEndDate"
                                value={formData.membershipEndDate}
                                onChange={handleChange}
                                min={formData.membershipStartDate}
                                disabled={loading}
                            />
                        </div>
                    </div>
                    


                    <div className="form-group full-width">
                        <label>
                            <FiTag />
                            <span>Education & Qualifications</span>
                        </label>
                        <div className="input-with-button">
                            <input
                                type="text"
                                value={newEdu}
                                onChange={(e) => setNewEdu(e.target.value)}
                                placeholder="Add qualification (e.g. MBBS, MD)"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="btn-add"
                                onClick={addEducation}
                                disabled={loading}
                            >
                                Add
                            </button>
                        </div>
                        {formData.education && formData.education.length > 0 && (
                            <div className="tags-container" style={{ marginTop: '10px' }}>
                                {formData.education.map((edu, index) => (
                                    <div key={index} className="tag">
                                        <span>{edu}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeEducation(index)}
                                            className="tag-remove"
                                            disabled={loading}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/doctors')}
                            className="btn-cancel"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span>Creating...</span>
                                </>
                            ) : (
                                <>
                                    <FiSave />
                                    <span>Create Doctor</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    );
}
